import { Controller } from "@hotwired/stimulus";

// Escapes text for safe interpolation into an HTML string.
function escapeHtml(text) {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

// Fetches an HTML fragment and injects it in place, replacing the loading
// placeholder. Used by the accommodation step to load each accommodation's rooms
// (availability scan + render) in parallel, off the initial page load.
//
// An empty response means "no available rooms", so the element removes itself.
// A heavily-carved room's availability scan can legitimately take tens of seconds,
// so we do NOT abort a slow-but-succeeding request: after `slow` ms we just show a
// reassuring message, and only a genuine hang (`timeout` ms) or a network/server
// error falls back to a retry.
export default class extends Controller {
  static values = {
    url: String,
    name: String,
    slowText: String,
    errorText: String,
    retryText: String,
    slow: { type: Number, default: 8000 },
    timeout: { type: Number, default: 90000 },
  };

  static targets = ["label"];

  connect() {
    this.load();
  }

  load() {
    if (this.inFlight) return;
    this.inFlight = true;

    const abort = new AbortController();
    this.slowTimer = setTimeout(() => this.markSlow(), this.slowValue);
    this.timeoutTimer = setTimeout(() => abort.abort(), this.timeoutValue);

    fetch(this.urlValue, {
      headers: { "X-Requested-With": "XMLHttpRequest" },
      credentials: "same-origin",
      signal: abort.signal,
    })
      .then((response) =>
        response.ok ? response.text() : Promise.reject(response.status)
      )
      .then((html) => {
        this.reset();
        const content = (html || "").trim();
        if (content === "") {
          this.element.remove();
        } else {
          this.element.innerHTML = content;
        }
      })
      .catch(() => {
        this.reset();
        this.renderError();
      });
  }

  reset() {
    this.inFlight = false;
    clearTimeout(this.slowTimer);
    clearTimeout(this.timeoutTimer);
  }

  markSlow() {
    if (this.hasLabelTarget && this.slowTextValue) {
      this.labelTarget.textContent = this.slowTextValue;
    }
  }

  renderError() {
    const heading = this.nameValue
      ? `<div class="font-medium text-gray-900">${escapeHtml(
          this.nameValue
        )}</div>`
      : "";
    this.element.innerHTML = `
      <div class="rounded-lg border border-gray-200 p-4">
        ${heading}
        <div class="mt-2 flex items-center gap-2 text-sm text-gray-500">
          <span>${escapeHtml(this.errorTextValue)}</span>
          <button type="button" class="text-blue-600 hover:text-blue-800 underline"
                  data-action="lazy-fragment-loader#load">${escapeHtml(
                    this.retryTextValue
                  )}</button>
        </div>
      </div>`;
  }
}
